import { useMemo } from "react";
import { GiHammerNails } from "react-icons/gi";
import {
  BuildingCatalogEntry,
  BuildingState,
  BuildOrder,
  ResourcePool,
} from "../types/castle";
import { CurrencyIcon, CurrencyKind } from "./CurrencyIcon";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";

// "Heart of Castle" — a dedicated full-screen upgrade surface for the
// central keep, opened instead of the generic BuildingDetailSheet when the
// player taps the keep. The keep is element-themed via a faceted CSS/SVG
// crystal that grows through visual stages as the Castle levels up. The
// upgrade logic mirrors the Castle branch of BuildingDetailSheet exactly
// (the Castle, type 0, is the only building not gated by Castle level), so
// this screen is purely a richer presentation of the same build flow.

// Per-element gem palette. shades run brightest (index 0, catches light)
// to darkest (index 4, in shadow); facets reference them to fake a lit
// gem. glow is an "r,g,b" string fed into rgba() for halo + spark colour.
const PALETTE: Record<
  number,
  { gem: string; shades: [string, string, string, string, string]; glow: string }
> = {
  0: { gem: "Рубин", shades: ["#ffe2d2", "#ff8f63", "#ff5a47", "#c0211a", "#6f0d10"], glow: "255,99,71" },
  1: { gem: "Сапфир", shades: ["#d8e6ff", "#7aa0ff", "#3d7bff", "#1f49c9", "#0e2563"], glow: "77,123,255" },
  2: { gem: "Алмаз", shades: ["#ffffff", "#e3f4ff", "#bfe9ff", "#86bcdf", "#4f7d9e"], glow: "190,232,255" },
  3: { gem: "Изумруд", shades: ["#cdffe6", "#62e39c", "#34d17a", "#149e54", "#0a5e30"], glow: "52,209,122" },
};

// Visual stages tied to Castle level. The gem silhouette is constant; the
// stage drives overall scale, halo strength and the count of orbiting
// shards so progression reads at a glance without per-level art.
const STAGES: Array<{ label: string; scale: number; shards: number; glow: number }> = [
  { label: "Осколок стихии", scale: 0.74, shards: 0, glow: 0.4 },
  { label: "Кристалл", scale: 0.86, shards: 2, glow: 0.55 },
  { label: "Огранённый", scale: 0.98, shards: 3, glow: 0.7 },
  { label: "Корона граней", scale: 1.08, shards: 5, glow: 0.85 },
  { label: "Сердце стихии", scale: 1.18, shards: 7, glow: 1 },
];

function stageFor(level: number): number {
  if (level >= 30) return 4;
  if (level >= 20) return 3;
  if (level >= 10) return 2;
  if (level >= 5) return 1;
  return 0;
}

// Inventory-backed speedup tiers (item ids from InventoryCatalog), mirrored
// from BuildingDetailSheet so the in-progress flow matches the rest of the
// castle. Free admin presets below are kept for the owner / Test World.
const inventorySpeedupTiers: Array<{ id: string; label: string }> = [
  { id: "spd-1m", label: "+1м" },
  { id: "spd-5m", label: "+5м" },
  { id: "spd-30m", label: "+30м" },
  { id: "spd-1h", label: "+1ч" },
  { id: "spd-8h", label: "+8ч" },
  { id: "spd-24h", label: "+24ч" },
];
const freeSpeedupPresets = [
  { label: "+1м", seconds: 60 },
  { label: "+5м", seconds: 5 * 60 },
  { label: "+30м", seconds: 30 * 60 },
  { label: "+1ч", seconds: 60 * 60 },
];

interface Props {
  element: number | undefined;
  state: BuildingState;
  def: BuildingCatalogEntry | undefined;
  activeOrder: BuildOrder | undefined;
  now: Date;
  resources: ResourcePool;
  buildSpeedReductionPct: number;
  inventory: Record<string, number>;
  activeBuildOrders: number;
  maxBuildSlots: number;
  onClose: () => void;
  onBuild: () => void;
  onSpeedup: (seconds: number) => void;
  onApplyInventorySpeedup: (itemId: string) => void;
  buildPending: boolean;
  speedupPending: boolean;
  error: string | null;
}

export function HeartOfCastle({
  element,
  state,
  def,
  activeOrder,
  now,
  resources,
  buildSpeedReductionPct,
  inventory,
  activeBuildOrders,
  maxBuildSlots,
  onClose,
  onBuild,
  onSpeedup,
  onApplyInventorySpeedup,
  buildPending,
  speedupPending,
  error,
}: Props) {
  const palette = PALETTE[element ?? 0] ?? PALETTE[0];
  const stageIdx = stageFor(state.level);
  const stage = STAGES[stageIdx];

  const nextLevel = state.level + 1;
  const isMax = def ? state.level >= def.maxLevel : false;
  const isUpgrading = !!activeOrder;
  const noFreeBuildSlots = !isUpgrading && activeBuildOrders >= maxBuildSlots;

  const levelDef = def?.levels.find((l) => l.level === nextLevel);
  const cost = levelDef?.cost;
  const canAfford = cost
    ? resources.gold >= cost.gold &&
      resources.wood >= cost.wood &&
      resources.stone >= cost.stone &&
      resources.mana >= cost.mana
    : false;
  const effectiveDurationSec = levelDef
    ? Math.ceil(levelDef.durationSeconds * (1 - Math.min(buildSpeedReductionPct, 0.9)))
    : 0;
  const remaining = activeOrder ? secondsUntil(activeOrder.finishesAt, now) : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-tg-bg"
      style={{
        background: `radial-gradient(120% 80% at 50% 18%, rgba(${palette.glow},0.16), var(--tg-theme-bg-color, #0e1621) 62%)`,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button
          type="button"
          onClick={() => {
            hapticImpact("light");
            onClose();
          }}
          aria-label="Назад"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-tg-secondaryBg text-tg-text active:bg-tg-bg"
        >
          ←
        </button>
        <div>
          <div className="text-lg font-semibold">Сердце замка</div>
          <div className="text-xs text-tg-hint">
            {palette.gem} · {stage.label}
          </div>
        </div>
      </div>

      {/* Crystal stage */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        <div
          className="heart-glow pointer-events-none absolute aspect-square w-[78%] max-w-[360px] rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(${palette.glow},${0.55 * stage.glow}) 0%, rgba(${palette.glow},0) 68%)`,
          }}
        />
        <CrystalGem palette={palette} stage={stage} stageIdx={stageIdx} />
      </div>

      {/* Stats + upgrade panel */}
      <div className="max-h-[58%] overflow-y-auto rounded-t-3xl bg-tg-bg/85 px-4 pb-5 pt-4 backdrop-blur-sm">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-tg-hint">Уровень замка</div>
            <div className="text-3xl font-bold leading-none">
              {state.level}
              {def ? <span className="text-base font-normal text-tg-hint"> / {def.maxLevel}</span> : null}
            </div>
          </div>
          <div className="text-right text-xs text-tg-hint">
            <div>Стадия кристалла</div>
            <div className="text-sm font-medium text-tg-text">
              {stageIdx + 1} / {STAGES.length}
            </div>
          </div>
        </div>

        {isUpgrading && activeOrder ? (
          <div className="mb-3 rounded-2xl bg-tg-secondaryBg p-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-tg-accent">Возвышение идёт</div>
            <div className="mb-2 text-sm">
              → Lvl {activeOrder.targetLevel} · {formatDuration(remaining)}
            </div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-tg-hint">
              Ускоритель из инвентаря
            </div>
            <div className="grid grid-cols-3 gap-2">
              {inventorySpeedupTiers.map((t) => {
                const count = inventory[t.id] ?? 0;
                const disabled = speedupPending || remaining <= 0 || count <= 0;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      hapticImpact("light");
                      onApplyInventorySpeedup(t.id);
                    }}
                    className={`flex flex-col items-center gap-0.5 rounded-lg bg-tg-bg px-2 py-2 text-xs disabled:opacity-30 ${
                      count > 0 ? "ring-1 ring-tg-accent/30" : ""
                    }`}
                  >
                    <span className="font-medium">{t.label}</span>
                    <span className="text-[10px] tabular-nums text-tg-hint">x{count}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-4 gap-1">
              {freeSpeedupPresets.map((p) => (
                <button
                  key={p.seconds}
                  type="button"
                  disabled={speedupPending || remaining <= 0}
                  onClick={() => {
                    hapticImpact("light");
                    onSpeedup(p.seconds);
                  }}
                  className="rounded-lg bg-tg-bg/40 px-1 py-1 text-[10px] text-tg-hint disabled:opacity-30"
                >
                  free {p.label}
                </button>
              ))}
            </div>
          </div>
        ) : isMax ? (
          <div className="mb-3 rounded-2xl bg-tg-secondaryBg p-3 text-sm text-tg-hint">
            Сердце замка достигло предела. Кристалл сияет в полную силу.
          </div>
        ) : noFreeBuildSlots ? (
          <div className="mb-3 rounded-2xl bg-tg-secondaryBg p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-tg-hint">Слоты постройки</div>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1.5">
                <GiHammerNails size={14} /> Все слоты заняты ({activeBuildOrders}/{maxBuildSlots}).
                Дождись окончания текущей стройки.
              </span>
            </div>
          </div>
        ) : (
          levelDef && (
            <div className="mb-3 rounded-2xl bg-tg-secondaryBg p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">Следующий уровень</div>
              <div className="mb-2 text-sm">→ Lvl {nextLevel}</div>
              {levelDef.effect ? (
                <div className="mb-2 text-xs text-tg-hint">{levelDef.effect}</div>
              ) : null}
              {cost && (
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <CostLine kind="gold" have={resources.gold} need={cost.gold} />
                  <CostLine kind="wood" have={resources.wood} need={cost.wood} />
                  <CostLine kind="stone" have={resources.stone} need={cost.stone} />
                  <CostLine kind="mana" have={resources.mana} need={cost.mana} />
                </div>
              )}
              <div className="mt-2 text-xs text-tg-hint">
                ⏱ {formatDuration(effectiveDurationSec)}
                {buildSpeedReductionPct > 0 && (
                  <span className="ml-1 text-tg-accent">
                    (−{Math.round(buildSpeedReductionPct * 100)}%)
                  </span>
                )}
              </div>
            </div>
          )
        )}

        {error && (
          <div className="mb-3 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {error}
          </div>
        )}

        {!isUpgrading && !isMax && !noFreeBuildSlots && cost && (
          <button
            type="button"
            disabled={!canAfford || buildPending}
            onClick={() => {
              hapticImpact("medium");
              onBuild();
              hapticNotification("success");
            }}
            className="w-full rounded-xl bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
          >
            {buildPending ? "Запускаю…" : `Поднять уровень → Lvl ${nextLevel}`}
          </button>
        )}
      </div>
    </div>
  );
}

// Faceted gem rendered from a fixed silhouette. Facet fills reference the
// element palette (lit from the upper-right). Orbiting shards and a moving
// specular sheen are driven by CSS keyframes defined in index.css.
function CrystalGem({
  palette,
  stage,
  stageIdx,
}: {
  palette: { shades: [string, string, string, string, string]; glow: string };
  stage: { scale: number; shards: number };
  stageIdx: number;
}) {
  const s = palette.shades;
  // [points, shadeIndex] — lit from upper-right, so right/upper facets are
  // brighter (low index) and left/lower facets sink into shadow (high index).
  const facets: Array<[string, number]> = [
    ["100,6 100,72 58,84", 2],
    ["100,6 142,84 100,72", 0],
    ["100,72 58,84 40,150 100,150", 3],
    ["100,72 100,150 160,150 142,84", 1],
    ["100,150 40,150 62,224 100,224", 4],
    ["100,150 100,224 138,224 160,150", 2],
    ["100,224 62,224 100,276", 4],
    ["100,224 100,276 138,224", 3],
  ];

  const shards = useMemo(() => {
    const out: Array<{ x: number; y: number; size: number; delay: number }> = [];
    for (let i = 0; i < stage.shards; i++) {
      const angle = (Math.PI * 2 * i) / stage.shards - Math.PI / 2;
      out.push({
        x: 50 + Math.cos(angle) * 44,
        y: 50 + Math.sin(angle) * 44,
        size: 7 + (i % 3) * 2,
        delay: (i % stage.shards) * 0.45,
      });
    }
    return out;
  }, [stage.shards]);

  return (
    <div
      className="relative aspect-[200/282] w-[58%] max-w-[260px]"
      style={{ transform: `scale(${stage.scale})` }}
    >
      <div className="heart-bob absolute inset-0">
      <svg viewBox="0 0 200 282" className="absolute inset-0 h-full w-full overflow-visible">
        <polygon
          points="100,6 142,84 160,150 138,224 100,276 62,224 40,150 58,84"
          fill="none"
          stroke={`rgba(${palette.glow},0.55)`}
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {facets.map(([points, shade], i) => (
          <polygon key={i} points={points} fill={s[shade]} stroke={s[4]} strokeWidth={0.6} strokeOpacity={0.5} />
        ))}
        {/* central spine highlight */}
        <polyline points="100,6 100,150 100,276" fill="none" stroke={s[0]} strokeOpacity={0.45} strokeWidth={1.2} />
        {/* moving specular sheen */}
        <polygon className="heart-sheen" points="100,6 142,84 100,150 100,72" fill="#ffffff" />
      </svg>

      {/* Orbiting shards */}
      {shards.map((sh, i) => (
        <span
          key={i}
          className="heart-shard absolute block"
          style={{
            left: `${sh.x}%`,
            top: `${sh.y}%`,
            width: sh.size,
            height: sh.size,
            marginLeft: -sh.size / 2,
            marginTop: -sh.size / 2,
            background: `linear-gradient(135deg, ${s[0]}, ${s[3]})`,
            boxShadow: `0 0 ${4 + stageIdx}px rgba(${palette.glow},0.8)`,
            animationDelay: `${sh.delay}s`,
          }}
        />
      ))}
      </div>
    </div>
  );
}

function CostLine({ kind, have, need }: { kind: CurrencyKind; have: number; need: number }) {
  const ok = have >= need;
  return (
    <div className={`flex items-center gap-1 ${ok ? "text-tg-text" : "text-tg-destructive"}`}>
      <CurrencyIcon kind={kind} size={14} />
      {formatNumber(need)}
    </div>
  );
}
